import { Ref } from 'preact';
import { MutableRef, useRef } from 'preact/hooks';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { classNames } from '../utils/UNSAFE_classNames';
import { useId } from '../hooks/UNSAFE_useId';
import { navigationListStyles } from './themes/NavigationListStyles.css';
import { useNavigationList } from './useNavigationList';
import { forwardRef, useImperativeHandle } from 'preact/compat';
import { useTestId, TestIdProps } from '../hooks/UNSAFE_useTestId';
import { NavigationListProps, NavigationListContext } from '../UNSAFE_NavigationListCommon';
export { RemovableNavigationListItem } from '../UNSAFE_NavigationListCommon';

export type FocusableHandle = {
  focus: () => void;
  blur: () => void;
};

export const NavigationList = forwardRef(
  <K extends string | number>(
    {
      children,
      selection,
      onSelectionChange,
      onRemove,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelLedBy,
      testId
    }: NavigationListProps<K> & TestIdProps,
    ref?: Ref<FocusableHandle>
  ) => {
    const containerRef = useRef<HTMLUListElement>(null);

    const { showFocusRing, currentKey, onCurrentKeyChange, containerHandlers, onKeyDown } =
      useNavigationList({
        containerRef: containerRef as MutableRef<HTMLUListElement>,
        selection,
        onRemove,
        onSelectionChange,
        children
      });

    const eventProps = { onKeyDown };
    const navigationListClass = classNames([navigationListStyles.uListStyle]);
    const navigationListItemPrefix = useId() + '_';
    const testIdProps = useTestId(testId);

    //Allows to call focus on rootRef without having to expose it
    useImperativeHandle(ref!, () => ({
      focus: () => {
        if (containerRef.current) {
          containerRef.current.focus();
        }
      },
      blur: () => {
        if (containerRef.current) {
          containerRef.current.focus();
        }
      }
    }));

    return (
      <ul
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelLedBy}
        role="tablist"
        ref={containerRef}
        aria-orientation={'vertical'}
        tabIndex={0}
        aria-activedescendant={currentKey ? navigationListItemPrefix + currentKey : ''}
        {...mergeProps(eventProps, containerHandlers)}
        {...testIdProps}
        class={navigationListClass}>
        <NavigationListContext.Provider
          value={{
            selection,
            onSelectionChange,
            onCurrentKeyChange,
            currentKey,
            showFocusRing,
            navigationListItemPrefix,
            onRemove
          }}>
          {children}
        </NavigationListContext.Provider>
      </ul>
    );
  }
);
